import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Trash, AlertTriangle } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ModelUtils, TemplatingItem } from '../../models';
import { useDeleteTemplatingItem } from '../../hooks/useDeleteTemplatingItem';
import { useTemplatingItemTypeDisplay } from '../../hooks/useTemplatingItemTypeDisplay';
import { useCanManageTemplates } from '../../hooks';

const useStyles = makeStyles(theme => ({
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  alert: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const messages = defineMessages({
  confirmTitle: {
    id: 'DeleteTemplatingItemButton.confirmTitle',
    defaultMessage: 'Delete {typeName}',
  },
  confirmMessage: {
    id: 'DeleteTemplatingItemButton.confirmMessage',
    defaultMessage:
      'Are you sure you want to delete this {typeName}? This action cannot be undone.',
  },
  templatingItemLabel: {
    id: 'DeleteTemplatingItemButton.templatingItemLabel',
    defaultMessage: '{typeName}:',
  },
  cancel: {
    id: 'DeleteTemplatingItemButton.cancel',
    defaultMessage: 'Cancel',
  },
  delete: {
    id: 'DeleteTemplatingItemButton.delete',
    defaultMessage: 'Delete',
  },
});

type DeleteTemplatingItemButtonProps = {
  templatingItem: TemplatingItem;
  typeName?: string;
};

export const DeleteTemplatingItemButton: React.FC<
  DeleteTemplatingItemButtonProps
> = ({ templatingItem }) => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { deleteTemplatingItem } = useDeleteTemplatingItem();
  const templatingItemTypeDisplay = useTemplatingItemTypeDisplay();
  const canManageTemplates = useCanManageTemplates();

  const openModal: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      setIsModalOpen(true);
    },
    [],
  );

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    await deleteTemplatingItem(templatingItem);
    setIsModalOpen(false);
  };

  const typeName = templatingItemTypeDisplay[templatingItem.type];

  if (!canManageTemplates) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label={formatMessage(messages.confirmTitle, { typeName })}
        icon={Trash}
        size={0}
        onClick={openModal}
        color="inverse"
      />
      <Modal
        isOpen={isModalOpen}
        onDismiss={handleCancel}
        title={formatMessage(messages.confirmTitle, { typeName })}
        actions={
          <>
            <Button variant="text" onClick={handleCancel}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              className={styles.deleteButton}
            >
              {formatMessage(messages.delete)}
            </Button>
          </>
        }
      >
        <Box direction="column" gap={3}>
          <Alert className={styles.alert} statusType="warning">
            <AlertIcon>
              <AlertTriangle />
            </AlertIcon>
            <AlertBody>
              <Text size="subbody">
                {formatMessage(messages.confirmMessage, { typeName })}
              </Text>
            </AlertBody>
          </Alert>

          <Box gap={1} direction="column">
            <Text size="caption" weight="bold" color="default">
              {formatMessage(messages.templatingItemLabel, { typeName })}
            </Text>
            <Text size="body">{ModelUtils.getName(templatingItem)}</Text>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
